const toZoneQty = (value) => {
  const parsed = Number.parseInt(String(value ?? 0), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getApiIssueAvailableQty = (apiItem) => {
  if (apiItem == null) {
    return 1;
  }
  const fromApi = toZoneQty(apiItem?.issue ?? apiItem?.Issue);
  return fromApi > 0 ? fromApi : 0;
};

export const hasApiIssueAvailable = (apiItem) =>
  getApiIssueAvailableQty(apiItem) > 0;

export const isPartialIssueEditable = (row) =>
  Boolean(row?._issueDraftActive && toZoneQty(row?._issueMaxNow) > 1);

export const getReissueIssueIncrementFromApi = (
  row,
  apiItem,
  { baselineBalance = 0 } = {}
) => {
  const apiQty = getApiIssueAvailableQty(apiItem);
  const sold = toZoneQty(row?.sold);
  const returnQty = toZoneQty(row?.return ?? row?.Return ?? row?.ReturnQty);
  const baseIssue = toZoneQty(row?.issue);
  const currentBalance = Math.max(0, baseIssue - sold - returnQty);
  const baselineBal = toZoneQty(baselineBalance);

  if (baselineBal > 0) {
    return apiQty;
  }

  return Math.max(0, apiQty - currentBalance);
};

export const initPartialIssueFromApi = (
  row,
  apiItem,
  { isReissue = false, baselineBalance = 0 } = {}
) => {
  const apiMaxBalance = getApiIssueAvailableQty(apiItem);
  const sold = toZoneQty(row?.sold);
  const returnQty = toZoneQty(row?.return ?? row?.Return ?? row?.ReturnQty);
  const baseIssue = isReissue ? toZoneQty(row?.issue) : 0;

  let issueNow;
  let maxNow;

  if (isReissue) {
    const remainingAllowed = getReissueIssueIncrementFromApi(row, apiItem, {
      baselineBalance,
    });

    if (remainingAllowed === 0) {
      const currentBalance = Math.max(0, baseIssue - sold - returnQty);
      return {
        ...row,
        sold,
        return: returnQty,
        balance: currentBalance,
      };
    }

    issueNow = remainingAllowed;
    maxNow = remainingAllowed;
  } else {
    maxNow = Math.max(1, apiMaxBalance);
    issueNow = maxNow;
  }

  const newIssue = baseIssue + issueNow;
  const draftActive = maxNow > 1;

  const next = {
    ...row,
    issue: newIssue,
    sold,
    return: returnQty,
    balance: newIssue - sold - returnQty,
    new_add: true,
    _issueBase: baseIssue,
    _issueNow: issueNow,
    _issueMaxNow: maxNow,
    _issueDraftActive: draftActive,
  };

  if (isReissue && !row?._changeSnapshot) {
    next._changeSnapshot = {
      issue: baseIssue,
      return: returnQty,
      balance: toZoneQty(row?.balance),
    };
  }

  return next;
};

export const applyPartialIssueNow = (row, rawIssueNow, { onMaxExceeded } = {}) => {
  if (!row?._issueDraftActive) {
    return row;
  }

  const maxNow = toZoneQty(row._issueMaxNow);
  const baseIssue = toZoneQty(row._issueBase);
  const parsed = String(rawIssueNow ?? '').trim();

  if (parsed === '') {
    return { ...row, _issueNow: '' };
  }

  let issueNow = toZoneQty(parsed);
  if (issueNow < 1) {
    issueNow = 1;
  }
  if (issueNow > maxNow) {
    onMaxExceeded?.(maxNow);
    issueNow = maxNow;
  }

  const sold = toZoneQty(row?.sold);
  const returnQty = toZoneQty(row?.return ?? row?.Return ?? row?.ReturnQty);
  const newIssue = baseIssue + issueNow;

  return {
    ...row,
    _issueNow: issueNow,
    issue: newIssue,
    balance: newIssue - sold - returnQty,
    new_add: true,
  };
};

export const stripPartialIssueDraftFields = (row) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const {
    _issueBase,
    _issueNow,
    _issueMaxNow,
    _issueDraftActive,
    ...rest
  } = row;
  return rest;
};

export const stripPartialIssueDraftFromItems = (items) =>
  (items || []).map(stripPartialIssueDraftFields);

export const prepareIssueItemsForSave = (items) =>
  stripPartialIssueDraftFromItems(items).map((row) => {
    const issueQty = toZoneQty(row?.issue ?? row?.Issue ?? row?.IssueQty);
    return {
      ...row,
      issue: issueQty,
      IssueQty: issueQty,
    };
  });
